import { Injectable, inject } from '@angular/core'
import { AuthService } from '../../auth/auth.service'
import { Observable, map } from 'rxjs'
import { SELLABLE_PLAN_IDS } from '../constants/plan-catalog'

export interface PlanFeatures {
  [key: string]: {
    trial: any
    launch: any
    pro: any
    scale: any
  }
}

/** Everything an upgrade surface needs to say which plan unlocks a feature. */
export interface UpgradeInfo {
  hasAccess: boolean
  currentPlan: string
  /** Cheapest plan that includes the feature — the one the upgrade CTA points at. */
  requiredPlan: string
  /** Every purchasable plan that includes the feature, cheapest first (e.g. ['pro', 'scale']). */
  plansWithAccess: string[]
}

@Injectable({
  providedIn: 'root',
})
export class FeatureAccessService {
  private authService = inject(AuthService)

  /**
   * Billing V2 matrix, mirroring the backend PlanLimits. `trial` is the 3-day
   * free trial: Scale-grade except for a 200 marketing-credit cap.
   */
  private planFeatures: PlanFeatures = {
    products: { trial: 'unlimited', launch: 'unlimited', pro: 'unlimited', scale: 'unlimited' },
    staffAccounts: { trial: 20, launch: 3, pro: 10, scale: 20 },
    themes: { trial: 'advancedPro', launch: 'basic', pro: 'advanced', scale: 'advancedPro' },
    themesCount: { trial: 'unlimited', launch: 2, pro: 5, scale: 'unlimited' },
    analytics: { trial: 'proAnalytics', launch: 'basic', pro: 'advanced', scale: 'proAnalytics' },
    automation: { trial: 'unlimited', launch: 'basic', pro: true, scale: 'unlimited' },
    abandonedCheckout: { trial: true, launch: false, pro: true, scale: true },
    discountEngine: { trial: 'full', launch: 'basic', pro: 'full', scale: 'full' },
    emails: { trial: 10000, launch: 0, pro: 5000, scale: 10000 },
    whatsapp: { trial: 10000, launch: 0, pro: 5000, scale: 10000 },
    campaignRecovery: { trial: true, launch: false, pro: true, scale: true },
    segmentation: { trial: 'advanced', launch: false, pro: true, scale: 'advanced' },
    multiMarket: { trial: 1, launch: false, pro: false, scale: 1 },
    staffPermissions: { trial: 'advanced', launch: 'basic', pro: 'advanced', scale: 'advanced' },
    reviews: { trial: 'advanced', launch: 'basic', pro: 'advanced', scale: 'advanced' },
    themeBuilder: { trial: true, launch: true, pro: true, scale: true },
    globalAccess: { trial: true, launch: false, pro: true, scale: true },
    receiveOrders: { trial: true, launch: true, pro: true, scale: true },
    paymentGateway: { trial: true, launch: true, pro: true, scale: true },
    conversionTracking: { trial: true, launch: true, pro: true, scale: true },
    customDomain: { trial: true, launch: true, pro: true, scale: true },
    apiKeys: { trial: true, launch: false, pro: true, scale: true },
    webhooks: { trial: true, launch: false, pro: true, scale: true },
    /* Billing V2 feature keys (mirror backend PlanLimits) */
    migrationTool: { trial: true, launch: false, pro: true, scale: true },
    advancedDiscounts: { trial: true, launch: false, pro: true, scale: true },
    automaticDiscounts: { trial: true, launch: false, pro: true, scale: true },
    abandonedCartTracking: { trial: true, launch: false, pro: true, scale: true },
    whatsappAutomations: { trial: true, launch: false, pro: true, scale: true },
    winBack: { trial: true, launch: false, pro: true, scale: true },
    reviewAutomation: { trial: true, launch: false, pro: true, scale: true },
    segmentCampaigns: { trial: true, launch: false, pro: true, scale: true },
    crossSellUpsell: { trial: true, launch: false, pro: true, scale: true },
    /* Scale-only: affiliate creator links, commissions and the payout report. */
    affiliates: { trial: true, launch: false, pro: false, scale: true },
    scheduledExports: { trial: true, launch: false, pro: true, scale: true },
    /* MCP / AI-assistant access — mirrors backend `canUseMcp` (gates POST /mcp + OAuth consent). */
    mcp: { trial: true, launch: true, pro: true, scale: true },
    /* COD Block Policy auto-block (carrier outcomes) + address/device hardening — mirrors backend `canUseCodAutoBlock`. */
    canUseCodAutoBlock: { trial: false, launch: false, pro: true, scale: true },
    emailDomain: { trial: 'brand', launch: 'taketheme', pro: 'brand', scale: 'brand' },
    marketingCredits: { trial: 200, launch: 1000, pro: 5000, scale: 15000 },
  }

  /**
   * Features whose value is a tier name rather than a boolean/quota, where
   * only one specific tier counts as "has access". Without this, the default
   * `!== false && !== 0` test would unlock `emailDomain` on every plan —
   * `'taketheme'` is a truthy value but means the *shared* sender, i.e. no
   * custom domain. Mirrors the backend's `checkEmailDomainAccess` gate.
   */
  private readonly requiredFeatureValue: Record<string, any> = {
    emailDomain: 'brand',
  }

  private resolveFeatureValue(featureName: string, plan: string): any {
    const feature = this.planFeatures[featureName]
    if (!feature) return null
    return feature[plan as keyof (typeof this.planFeatures)[string]]
  }

  /**
   * Does this plan's value for the feature count as access? With a `level`
   * (e.g. `discountEngine` at `'full'`), the value has to reach that tier —
   * Launch's `'basic'` discounts are truthy but don't unlock automatic ones.
   */
  private satisfies(featureName: string, planValue: any, level?: string): boolean {
    if (level) return this.meetsLevel(planValue, level)
    const required = this.requiredFeatureValue[featureName]
    if (required !== undefined) return planValue === required
    return planValue !== false && planValue !== 0
  }

  /**
   * Get the current user's subscription plan
   */
  getCurrentPlan(): Observable<string> {
    // No subscription yet = a brand-new store whose trial the backend seeds lazily.
    return this.authService.subscription.pipe(map((sub: any) => sub?.plan?.level?.toLowerCase() || 'trial'))
  }

  /**
   * Check if a feature is available in the current plan
   */
  hasFeature(featureName: string): Observable<boolean> {
    return this.getCurrentPlan().pipe(
      map(plan => {
        if (!this.planFeatures[featureName]) return false

        return this.satisfies(featureName, this.resolveFeatureValue(featureName, plan))
      }),
    )
  }

  /**
   * Get the feature value for the current plan
   */
  getFeatureValue(featureName: string): Observable<any> {
    return this.getCurrentPlan().pipe(map(plan => this.resolveFeatureValue(featureName, plan)))
  }

  /**
   * Check if feature limit has been reached
   */
  hasReachedLimit(featureName: string, currentUsage: number): Observable<boolean> {
    return this.getFeatureValue(featureName).pipe(
      map(limit => {
        if (limit === 'unlimited' || limit === true) return false
        if (typeof limit === 'number') {
          return currentUsage >= limit
        }
        return false
      }),
    )
  }

  /**
   * Cheapest purchasable plan that unlocks the feature (at `level`, when
   * given) — the plan every upgrade CTA names.
   */
  getRequiredPlan(featureName: string, level?: string): string {
    return this.getPlansWithAccess(featureName, level)[0] ?? 'scale'
  }

  /**
   * Check if user can access a specific feature level
   */
  canAccessLevel(featureName: string, requiredLevel: string): Observable<boolean> {
    return this.getFeatureValue(featureName).pipe(map(currentLevel => this.meetsLevel(currentLevel, requiredLevel)))
  }

  private meetsLevel(value: any, requiredLevel: string): boolean {
    if (value === 'unlimited' || value === true) return true

    const levels = ['basic', 'advanced', 'advancedPro', 'proAnalytics', 'full']
    const currentIndex = levels.indexOf(value)
    const requiredIndex = levels.indexOf(requiredLevel)

    if (currentIndex === -1 || requiredIndex === -1) return false

    return currentIndex >= requiredIndex
  }

  /**
   * Purchasable plans (cheapest first) whose value for the feature counts as
   * access. The first one is what upgrade prompts ask for; the rest are the
   * "also included in" plans.
   */
  getPlansWithAccess(featureName: string, level?: string): string[] {
    if (!this.planFeatures[featureName]) return []
    return SELLABLE_PLAN_IDS.filter(plan => this.satisfies(featureName, this.resolveFeatureValue(featureName, plan), level))
  }

  /**
   * Current plan vs. what the feature needs, for upgrade prompts and lock chips.
   */
  getUpgradeInfo(featureName: string, level?: string): Observable<UpgradeInfo> {
    return this.getCurrentPlan().pipe(
      map(currentPlan => ({
        hasAccess: this.satisfies(featureName, this.resolveFeatureValue(featureName, currentPlan), level),
        currentPlan,
        requiredPlan: this.getRequiredPlan(featureName, level),
        plansWithAccess: this.getPlansWithAccess(featureName, level),
      })),
    )
  }
}
